import { DocumentSigningStatus } from '@/components/dashboard/signatures';
import { EmptyFileState } from '@/components/shared/files/EmptyFileState';
import { FileListItem } from '@/components/shared/files/FileListItem';
import { FilePreview } from '@/components/shared/files/FilePreview';
import { CenteredSpinner } from '@/components/shared/Spinner';
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileType } from '@/hooks/queries/useFileQueries';
import {
  PackageFileDetails,
  useSignatureQueries
} from '@/hooks/queries/useSignatureQueries';
import { toast } from "@/hooks/useToast";
import { DialogDescription } from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { CheckCircle2 } from "lucide-react";
import { Suspense, useState } from 'react';

export const Route = createFileRoute('/$packageId/$signerId')({
  component: SigningPageWrapper,
});

function SigningPageWrapper() {
  const { packageId, signerId } = Route.useParams();
  return <SigningPage packageId={packageId} signerId={signerId} />;
}
function InvalidPackageOrDoneSigning() {
  return (
    <Card className="max-w-3xl mx-auto my-8">
      <CardHeader>
        <CardTitle>Document Signing Unavailable</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p>This document package is no longer available for signing or doesn't exist.</p>
        <p>Please check your email for the latest signing instructions or contact the document sender for assistance.</p>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button variant="outline" onClick={() => window.location.href = '/'}>
          Return Home
        </Button>
      </CardFooter>
    </Card>
  );
}

function SigningPage({ packageId, signerId }: { packageId: string; signerId: string }) {
  const { useFetchPackage, createSignatureMutation: createSignature } = useSignatureQueries();
  const { data: pkg } = useFetchPackage(packageId);

  const queryClient = useQueryClient();

  const signer = pkg.signers.find((s) => s.signer_id === signerId);

  const [selectedPackageFile, setSelectedPackageFile] = useState<PackageFileDetails>(pkg.package_files[0]);

  const signature = selectedPackageFile.signatures.find((v) => v.signer_id === signerId)

  const [selectedFile, setSelectedFile] = useState<FileType>(signature?.file || pkg.package_files[0].file);

  const [isCompletionDialogOpen, setCompletionDialogOpen] = useState<boolean>(false);


  const signedFilesCount = pkg.package_files.reduce((count, packageFile) => {
    return packageFile.signatures.some((signature) => signature.signer_id === signerId)
      ? count + 1
      : count;
  }, 0);

  const handleSign = async ({ file, blob }: { file: FileType, blob: Blob }) => {
    if (!signer) return;
    await createSignature.mutateAsync({
      signing_package: pkg,
      package_file: selectedPackageFile,
      signer,
      file,
      blob
    }, {
      onSuccess: (signature) => {
        toast({
          title: 'Document signed successfully',
          description: 'Thank you for signing this document.',
        });
        setSelectedPackageFile({
          ...selectedPackageFile,
          signatures: [
            ...selectedPackageFile.signatures,
            signature
          ]
        })
        setSelectedFile(signature.file)
        queryClient.invalidateQueries({ queryKey: ['package', packageId] })
        if (signedFilesCount === pkg.package_files.length) {
          setCompletionDialogOpen(true);
        }
      },
      onError: (error) => {
        console.error('Error signing document:', error);
        toast({
          title: 'Failed to sign document',
          description: error.message || 'An error occurred while signing the document.',
          variant: 'destructive',
        });
      }
    });
  };

  const [isTermsOpen, setTermsOpen] = useState(true);

  if (!signer) {
    return <InvalidPackageOrDoneSigning />
  }

  return (
    <div className="max-w-6xl mx-auto my-8 px-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
        <AlertDialog open={isTermsOpen} onOpenChange={setTermsOpen}>
          <AlertDialogContent className="max-w-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm TOS before continuing</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogDescription className="space-y-4">
              By clicking "Agree", you confirm that you've read and accept the
              <Link to="/swim-sign-tos" className="text-primary font-medium hover:underline mx-1">
                Swim Sign Terms of Service
              </Link>
              and acknowledge that electronic signatures are legally binding.
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setTermsOpen(false)}>
                Agree
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Card className="flex flex-col h-full overflow-hidden col-span-1">
          <CardHeader>
            <CardTitle className="flex justify-between items-center">
              <div>Document Signing</div>
            </CardTitle>
            <div className="text-sm text-muted-foreground">
              {signer.name ? `Welcome, ${signer.name}` : signer.email}
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-hidden flex flex-col space-y-4">
            <DocumentSigningStatus
              totalFiles={pkg.package_files.length}
              signedFiles={signedFilesCount}
            />

            <div className="flex-1 space-y-2 overflow-auto">
              {pkg.package_files.map((packageFile) => {
                const signature = packageFile.signatures.find((v) => v.signer_id === signerId)
                const file = signature?.file ?? packageFile.file
                return (
                  <FileListItem
                    key={packageFile.package_file_id}
                    file={packageFile.file}
                    isSelected={selectedPackageFile.package_file_id === packageFile.package_file_id}
                    onSelect={() => {
                      setSelectedPackageFile(packageFile)
                      setSelectedFile(file)
                    }}
                    signStatus={signature ? 'signed' : 'unsigned'}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="h-full max-h-full col-span-1 lg:col-span-2 overflow-hidden">
          {selectedFile ? (
            <Suspense fallback={<CenteredSpinner />}>
              <FilePreview
                file={selectedFile}
                onSave={signature ? undefined : handleSign}
              />
            </Suspense>
          ) : (
            <EmptyFileState
              title="No file selected"
              description="Select a file from the list to view and sign"
            />
          )}
        </Card>
      </div>

      {/* Completion Dialog */}
      <Dialog open={isCompletionDialogOpen} onOpenChange={setCompletionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Signing Process</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            You have signed all the documents in this package, and an email confirmation has been sent to all parties. You can come back to this page at any time to review or download your documents. 
            If you have any quesitons, please email <a href="mailto:support@swimhomes.com">support@swimhomes.ca</a>
          </DialogDescription>
          <DialogFooter>
            <Button onClick={() => setCompletionDialogOpen(false)}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
